import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { Modal } from 'reactstrap';
import PropTypes from 'prop-types';
import moment from 'moment';
import GroupEditModalForm from './GroupEditModalForm';
import config from '../../../../config';
import Notify from '../../../../shared/components/widgets/Notify';
import { PLATFORM_BUDGET_TYPES } from '../../shared/constants';
import { serializeToBudgetJson, deserializeFromBudgetJson } from '../../shared/budgetJsonUtils';

const GroupEditModal = ({ currentEditRow, changeShowEditModal, showEditModal, setForceRefresh, budgetAccountData, activeDayOptions, disableButton, setDisableButton, setShowEditModal, retailerConfig }) => {
  const sellerSelect = useSelector(store => store.sellerSelect);
  const user = useSelector(store => store.authentication.user);
  const theme = useSelector(store => store.theme);
  const [catalogOnItems, setCatalogOnItems] = useState('');

  // Fetch ON items from catalog for Item budgets when editing (only when modal is open)
  useEffect(() => {
    if (showEditModal && currentEditRow && currentEditRow.budget_group_name && currentEditRow.budget_type === 'item' && retailerConfig.key === 'amazon') {
      const catalogUrl = `${config.serverUrl}/customers/amazon/catalog?sellerAccountId=${sellerSelect.value}&parentAccountId=${sellerSelect.parentAccountId}`;
      const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };

      fetch(catalogUrl, { headers: headerWithAuth })
        .then(response => response.json())
        .then(data => {
          // Check if data and data.results exist before filtering
          if (data && data.results && Array.isArray(data.results)) {
            const onItems = data.results
              .filter(item => item.budget_group === currentEditRow.budget_group_name && item.ad_status?.toUpperCase() === 'ON')
              .map(item => (sellerSelect.type === 1 ? item.sku : item.asin))
              .filter(item => item)
              .join(',');

            setCatalogOnItems(onItems);
          } else {
            console.warn('Catalog data is missing or has unexpected format:', data);
            setCatalogOnItems('');
          }
        })
        .catch(error => {
          console.error('Error fetching catalog data:', error);
          setCatalogOnItems('');
        });
    } else {
      setCatalogOnItems('');
    }
  }, [showEditModal, currentEditRow, sellerSelect.value, sellerSelect.parentAccountId, sellerSelect.type, user.jwtToken, retailerConfig.key]);

  const monthOptions = [

    { label: 'January', name: 'january', value: '1' },
    { label: 'February', name: 'february', value: '2' },
    { label: 'March', name: 'march', value: '3' },
    { label: 'April', name: 'april', value: '4' },
    { label: 'May', name: 'may', value: '5' },
    { label: 'June', name: 'june', value: '6' },
    { label: 'July', name: 'july', value: '7' },
    { label: 'August', name: 'august', value: '8' },
    { label: 'September', name: 'september', value: '9' },
    { label: 'October', name: 'october', value: '10' },
    { label: 'November', name: 'november', value: '11' },
    { label: 'December', name: 'december', value: '12' },
  ];

  // Helper function to get spend speed label
  const getSpendSpeedLabel = (value) => {
    if (value === 'speed_level_1') return 'Standard';
    if (value === 'speed_level_2') return 'Fast';
    return 'Fastest';
  };

  // Helper function to get limitless budget label
  const getLimitlessBudgetLabel = (value) => {
    if (value === 0) return 'Strict Budget';
    if (value === 1) return 'No Budget';
    return 'Flexible Budget';
  };

  // Helper function to get spend priority label
  const getSpendPriorityLabel = (value) => {
    if (value === '-2') return 'Most Brand';
    if (value === '-1') return 'More Brand';
    if (value === '0') return 'Brand and NonBrand';
    if (value === '1') return 'More NonBrand';
    return 'Most NonBrand';
  };

  // Transform currentEditRow from database format to form format
  const getInitialValues = () => {
    // Check if user is admin
    const isAdmin = user.access === 'admin';

    if (!currentEditRow || !currentEditRow.budget_group_name) {
      // New budget - add platform-specific defaults
      return {
        ...currentEditRow,
        setupFlow: { label: 'Advanced Setup', value: 'advanced' },
        agencyInternal: isAdmin ? { label: 'Limitless View', value: true } : { label: 'Agency View', value: false },
        ...(retailerConfig.key === 'google' && {
          spendTactic: { label: 'Purchase Full Funnel SA', value: 'purchase_full_funnel_sa' },
          googleLanguages: 'languageConstants/1000',
        }),
        ...(retailerConfig.key === 'tiktok' && {
          tiktokLanguages: [{ label: 'English (US)', value: 'en_US' }],
        }),
        ...(retailerConfig.key === 'meta' && {
          metaLanguages: [{ label: 'English (US)', value: 'en_US' }],
          spendTactic: { label: 'Purchase Full Funnel SA', value: 'purchase_full_funnel_sa' },
        }),

      };
    }

    // Parse active days CSV into individual day booleans
    const activeDays = {};
    if (currentEditRow.active_days_csv) {
      const daysArray = currentEditRow.active_days_csv.split(',');
      activeDayOptions.forEach((day, index) => {
        activeDays[day.name] = daysArray[index] === '1';
      });
    }

    // Parse budget_json
    let budgetJson = {};
    try {
      budgetJson = JSON.parse(currentEditRow.budget_json || '{}');
    } catch (e) {
      console.error('Error parsing budget_json:', e);
    }

    // Get allocation percentages with backwards compatibility
    // For older budget groups without allocations, default to 100% SP+SD
    let allocationSpSd = budgetJson.allocation?.percentages?.sp_sd_percent;
    let allocationSb = budgetJson.allocation?.percentages?.sb_percent;
    let allocationSt = budgetJson.allocation?.percentages?.st_percent;

    // Backwards compatibility: If no allocations exist (older budget groups), default to 100% SP+SD
    const hasAllocations = allocationSpSd !== undefined || allocationSb !== undefined || allocationSt !== undefined;
    if (!hasAllocations) {
      allocationSpSd = 100;
      allocationSb = 0;
      allocationSt = 0;
    } else {
      // Use existing values or default to 0 if partially defined
      allocationSpSd = allocationSpSd || 0;
      allocationSb = allocationSb || 0;
      allocationSt = allocationSt || 0;
    }

    // Get original daily and monthly budgets with backwards compatibility
    // New structure: budgetJson.allocation.original.daily.budget
    // Old structure: budgetJson.daily.budget
    let baseDailyBudgetAmount = budgetJson.allocation?.original?.daily?.budget;
    if (baseDailyBudgetAmount === undefined || baseDailyBudgetAmount === null) {
      // Backwards compatibility: Try old structure for older budget groups
      baseDailyBudgetAmount = budgetJson.daily?.budget || 0;
    }

    const reallocation = budgetJson.allocation?.original?.daily?.reallocation || budgetJson.daily?.reallocation || 0;

    // Get monthly budgets with backwards compatibility
    // New structure: budgetJson.allocation.original.monthly[monthName] (direct value)
    // Old structure: budgetJson.monthly[monthNumber].budget (nested object)
    const monthlyBudgets = {};
    monthOptions.forEach(month => {
      let monthValue = budgetJson.allocation?.original?.monthly?.[month.name];
      if (monthValue === undefined || monthValue === null) {
        // Backwards compatibility: Try old structure with month number (1-12)
        // Old structure stores as: budgetJson.monthly["1"] = { name: "january", budget: 1000 }
        if (budgetJson.monthly?.[month.value]?.budget !== undefined) {
          monthValue = budgetJson.monthly[month.value].budget;
        } else {
          monthValue = 0;
        }
      }
      monthlyBudgets[month.name] = monthValue;
    });

    // Build select option objects using helper functions
    // Database uses 'form_type' but form uses 'setup_flow'
    const formTypeValue = currentEditRow.form_type || currentEditRow.setup_flow || 'quick';
    const setupFlowLabel = formTypeValue === 'quick' ? 'Quick Setup' : 'Advanced Setup';
    const agencyInternalLabel = currentEditRow.agency_internal ? 'Limitless View' : 'Agency View';
    const costTargetTypeLabel = currentEditRow.cost_target_type === 'bnb' ? 'Brand/NonBrand' : 'Blended';

    // Get spend tactic label with support for Google-specific value
    const getSpendTacticLabel = (value) => {
      if (value === 'max_perf') return 'Max Performance';
      if (value === 'full_spend') return 'Full Budget Spend';
      if (value === 'purchase_full_funnel_sa') return 'Purchase Full Funnel SA';
      return 'Max Performance'; // fallback
    };
    const spendTacticLabel = getSpendTacticLabel(currentEditRow.spend_tactic);

    // For Item budgets: ON items come from catalog API, OFF items come from table
    // For Brand budgets: Both ON and OFF come from table
    // For DSP budgets: master_item_on_csv contains the landing page URL
    const isItemBudget = currentEditRow.budget_type === 'item';
    const isDspBudget = currentEditRow.budget_type?.toLowerCase() === 'dsp';
    const isBrandBudget = currentEditRow.budget_type === 'brand';

    let onItemsList = '';
    if (isItemBudget && catalogOnItems) {
      // Item budgets: use catalog API data
      onItemsList = catalogOnItems;
    } else if (isDspBudget || isBrandBudget) {
      // DSP/Brand budgets: use database value directly
      onItemsList = currentEditRow.master_item_on_csv || currentEditRow.master_item_csv || '';
    } else {
      // Fallback for item budgets without catalog data
      onItemsList = currentEditRow.master_item_on_csv || currentEditRow.master_item_csv || '';
    }

    // Parse Brand-specific fields from database columns for Brand budgets
    const brandFields = {};
    if (isBrandBudget) {
      // Brand Ad Format - comes from ad_format column
      if (currentEditRow.ad_format) {
        const formatMap = {
          product_collection: 'Product Collection',
          store_spotlight: 'Store Spotlight',
        };
        const formatLabel = formatMap[currentEditRow.ad_format] || currentEditRow.ad_format;
        brandFields.brandAdFormat = { label: formatLabel, value: currentEditRow.ad_format };
      } else {
        // Default if not set
        brandFields.brandAdFormat = { label: 'Product Collection', value: 'product_collection' };
      }

      // Brand dropdown value - initialize with current data, will be matched by BrandBudgetFields
      // when brands load from API
      if (currentEditRow.brand_name && currentEditRow.brand_entity_id) {
        brandFields.brandName = currentEditRow.brand_name;
        brandFields.brandEntityId = currentEditRow.brand_entity_id;
        // Initialize the brand dropdown with a placeholder that will be replaced when brands load
        brandFields.brand = {
          label: currentEditRow.brand_name,
          value: currentEditRow.brand_id || currentEditRow.brand_entity_id,
          brandEntityId: currentEditRow.brand_entity_id,
        };
      }
    }

    // Parse DSP-specific fields from database columns for DSP budgets
    const dspFields = {};
    if (isDspBudget) {
      // Check if ad_format is in budget_json.dsp (if not in row)
      let adFormatValue = currentEditRow.ad_format;
      if (!adFormatValue && budgetJson.dsp?.ad_format) {
        adFormatValue = budgetJson.dsp.ad_format;
      }

      // DSP Ad Format - comes from ad_format column OR budget_json.dsp.ad_format
      if (adFormatValue) {
        const formatLabel = adFormatValue.charAt(0).toUpperCase() + adFormatValue.slice(1);
        dspFields.dspAdFormat = { label: formatLabel, value: adFormatValue };
      } else {
        // Default if not set
        dspFields.dspAdFormat = { label: 'Display', value: 'display' };
      }

      // Gender Targeting - comes from includes column (repurposed for DSP)
      if (currentEditRow.includes) {
        // Map database values to DSP gender options
        const genderMap = {
          male_and_female: 'Male and Female',
          male: 'Male',
          female: 'Female',
        };
        const genderLabel = genderMap[currentEditRow.includes] || currentEditRow.includes;
        dspFields.dspGender = { label: genderLabel, value: currentEditRow.includes };
      } else {
        // Default if not set
        dspFields.dspGender = { label: 'Male and Female', value: 'male_and_female' };
      }

      // DSP Spend Tactic - comes from spend_tactic column (uses DSP-specific options)
      if (currentEditRow.spend_tactic) {
        // Map database values to DSP spend tactic options
        const tacticMap = {
          all_full_funnel: 'ALL - Full Funnel',
          top_of_funnel: 'Top of Funnel',
          middle_of_funnel: 'Middle of Funnel',
          bottom_of_funnel: 'Bottom of Funnel',
          loyalty_retargeting_funnel: 'Loyalty Retargeting Funnel',
        };
        const tacticLabel = tacticMap[currentEditRow.spend_tactic] || currentEditRow.spend_tactic;
        dspFields.dspSpendTactic = { label: tacticLabel, value: currentEditRow.spend_tactic };
      } else {
        // Default if not set
        dspFields.dspSpendTactic = { label: 'ALL - Full Funnel', value: 'all_full_funnel' };
      }

      // DSP Spend Speed - comes from spend_speed column (uses DSP-specific options)
      if (currentEditRow.spend_speed) {
        // Map database values to DSP spend speed options
        const speedMap = {
          auto_all: 'Auto (ALL)',
          aoo: 'AOO',
          apd: 'APD',
          '3pe': '3PE',
        };
        const speedLabel = speedMap[currentEditRow.spend_speed] || currentEditRow.spend_speed;
        dspFields.dspSpendSpeed = { label: speedLabel, value: currentEditRow.spend_speed };
      } else {
        // Default if not set
        dspFields.dspSpendSpeed = { label: 'Auto (ALL)', value: 'auto_all' };
      }

      // DSP Item Type - comes from item_type column
      if (currentEditRow.item_type) {
        // Map database values to DSP item type options
        const itemTypeMap = {
          product_page: 'Product Page',
          custom_landing_page: 'Custom Landing Page',
          brand_collection: 'Brand Collection',
          product_collection: 'Product Collection',
          store: 'Store',
        };
        const itemTypeLabel = itemTypeMap[currentEditRow.item_type] || currentEditRow.item_type;
        dspFields.dspItemType = { label: itemTypeLabel, value: currentEditRow.item_type };
      } else {
        // Default if not set
        dspFields.dspItemType = { label: 'Product Page', value: 'product_page' };
      }
    }

    // Only exclude day properties from currentEditRow if we're parsing them from active_days_csv
    // For new budgets without active_days_csv, we want to keep the default day values
    let baseEditRow;
    if (currentEditRow.active_days_csv) {
      // Editing existing budget - exclude day properties since we'll use parsed activeDays
      const { monday, tuesday, wednesday, thursday, friday, saturday, sunday, ...currentEditRowWithoutDays } = currentEditRow;
      baseEditRow = currentEditRowWithoutDays;
    } else {
      // New budget - keep all properties including default day values
      baseEditRow = currentEditRow;
    }

    const finalValues = {
      ...baseEditRow,
      budgetGroupName: currentEditRow.budget_group_name,
      budgetType: (() => {
        // For Google budgets, try to find the option by ad_type or budget_type
        if (retailerConfig.key === 'google') {
          // First try to map from ad_type if available
          if (currentEditRow.ad_type) {
            const adTypeMap = {
              SEARCH: 'google_search',
              'PERFORMANCE MAX': 'google_pmax',
              'STANDARD SHOPPING': 'google_shop',
            };
            const googleValue = adTypeMap[currentEditRow.ad_type];
            if (googleValue) {
              const option = PLATFORM_BUDGET_TYPES?.google?.options?.find(opt => opt.value === googleValue);
              if (option) {
                return { label: option.label, value: googleValue };
              }
            }
          }

          // If ad_type isn't available, try to find by budget_type directly
          if (currentEditRow.budget_type) {
            const option = PLATFORM_BUDGET_TYPES?.google?.options?.find(opt => opt.value === currentEditRow.budget_type);
            if (option) {
              return { label: option.label, value: option.value };
            }
          }
        }

        if (!currentEditRow.budget_type) {
          return { label: 'Item', value: 'item' };
        }
        if (currentEditRow.budget_type.toUpperCase() === 'DSP') {
          return { label: 'DSP', value: currentEditRow.budget_type };
        }
        return {
          label: currentEditRow.budget_type.charAt(0).toUpperCase() + currentEditRow.budget_type.slice(1),
          value: currentEditRow.budget_type,
        };
      })(),
      setupFlow: { label: setupFlowLabel, value: formTypeValue },
      agencyInternal: (() => {
        if (typeof currentEditRow.agency_internal === 'boolean') {
          return { label: agencyInternalLabel, value: currentEditRow.agency_internal };
        }
        if (isAdmin) {
          return { label: 'Limitless View', value: true };
        }
        return { label: 'Agency View', value: false };
      })(),
      portfolioIdsCsv: currentEditRow.portfolio_ids_csv || '',
      // For non-DSP budgets, includes is 'limitless' or ''; for DSP budgets, includes stores gender so we handle it separately
      // For Other budget type with DSP ad type, default to 'external' (Manual ADs)
      includes: (() => {
        if (isDspBudget) {
          // DSP budget type uses includes for gender, handled separately
          return { label: 'All ADs', value: '' };
        }
        if (currentEditRow.budget_type?.toLowerCase() === 'other' && currentEditRow.ad_type_csv === 'DSP') {
          // Other budget type with DSP ad type should default to Manual ADs
          return { label: 'Manual ADs', value: 'external' };
        }
        if (currentEditRow.includes) {
          return { label: 'Limitless ADs', value: 'limitless' };
        }
        return { label: 'All ADs', value: '' };
      })(),
      costTargetType: currentEditRow.cost_target_type ? { label: costTargetTypeLabel, value: currentEditRow.cost_target_type } : { label: 'Blended', value: 'blended' },
      costTargetMetric: 'roas',
      costTarget: currentEditRow.cost_target_brand || 0,
      costTargetBrand: currentEditRow.cost_target_brand || 0,
      costTargetNonBrand: currentEditRow.cost_target_nonbrand || 0,
      // For non-DSP budgets, use regular spend speed/tactic; for DSP they're handled in dspFields
      spendSpeed: !isDspBudget && currentEditRow.spend_speed ? { label: getSpendSpeedLabel(currentEditRow.spend_speed), value: currentEditRow.spend_speed } : { label: 'Standard', value: 'speed_level_1' },
      spendTactic: (() => {
        if (isDspBudget) return null; // DSP has its own field
        if (currentEditRow.spend_tactic) {
          return { label: spendTacticLabel, value: currentEditRow.spend_tactic };
        }
        // Default based on retailer
        if (retailerConfig.key === 'google' || retailerConfig.key === 'meta') {
          return { label: 'Purchase Full Funnel SA', value: 'purchase_full_funnel_sa' };
        }
        return { label: 'Max Performance', value: 'max_perf' };
      })(),

      limitlessBudget: currentEditRow.limitless_budget !== undefined ? { label: getLimitlessBudgetLabel(currentEditRow.limitless_budget), value: currentEditRow.limitless_budget } : { label: 'Strict Budget', value: 0 },
      spendPriority: currentEditRow.spend_priority ? { label: getSpendPriorityLabel(currentEditRow.spend_priority), value: currentEditRow.spend_priority } : { label: 'Brand and NonBrand', value: '0' },
      // For non-DSP budgets, use regular item type; for DSP it's handled in dspFields
      itemType: (() => {
        if (!isDspBudget && currentEditRow.item_type) {
          return { label: currentEditRow.item_type, value: currentEditRow.item_type };
        }
        if (sellerSelect.type === 1) {
          return { label: 'SKU', value: 'SKU' };
        }
        return { label: 'ASIN', value: 'ASIN' };
      })(),
      itemSelection: currentEditRow.item_selection ? { label: currentEditRow.item_selection, value: currentEditRow.item_selection } : { label: 'All', value: 'ALL' },
      destination: currentEditRow.destination ? { label: currentEditRow.destination, value: currentEditRow.destination } : { label: 'On', value: 'ON' },
      // masterItemOnList holds the landing page URL for DSP budgets
      masterItemOnList: onItemsList,
      masterItemOffList: currentEditRow.master_item_off_csv || '',
      masterItemOrigList: currentEditRow.master_item_orig_csv || currentEditRow.master_item_csv || '',
      startDate: currentEditRow.start_date,
      endDate: currentEditRow.end_date,
      budgetDriver: currentEditRow.budget_driver ? { label: currentEditRow.budget_driver.charAt(0).toUpperCase() + currentEditRow.budget_driver.slice(1), value: currentEditRow.budget_driver } : { label: 'Daily', value: 'daily' },
      state: currentEditRow.state ? { label: currentEditRow.state.charAt(0).toUpperCase() + currentEditRow.state.slice(1), value: currentEditRow.state } : { label: 'Enabled', value: 'enabled' },
      budgetAllocationSpSd: allocationSpSd,
      budgetAllocationSb: allocationSb,
      budgetAllocationSt: allocationSt,
      baseDailyBudgetAmount,
      reallocation,
      ...monthlyBudgets,
      budgetGroupRecords: (() => {
        // Check if this is an External budget group
        const isExternalGroup = currentEditRow.budget_group_name && currentEditRow.budget_group_name.trim().endsWith('- External');

        if (isExternalGroup) {
          // For External groups, rebuild records to have only the detected ad type
          const budgetName = currentEditRow.budget_group_name;
          let externalAdType = 'SP'; // Default

          if (budgetName.includes('Sponsored Brands')) {
            externalAdType = 'SB';
          } else if (budgetName.includes('Sponsored Products')) {
            externalAdType = 'SP';
          } else if (budgetName.includes('Sponsored Display')) {
            externalAdType = 'SD';
          } else if (budgetName.includes('Sponsored Television')) {
            externalAdType = 'ST';
          } else if (budgetName.includes('DSP')) {
            externalAdType = 'DSP';
          }

          // Find the existing ID for this ad type from budgetGroupRecords
          let existingId = 0;
          if (currentEditRow.budgetGroupRecords && currentEditRow.budgetGroupRecords.length > 0) {
            // Find the record matching the detected ad type
            const matchingRecord = currentEditRow.budgetGroupRecords.find(r => r.ad_type === externalAdType);
            if (matchingRecord && matchingRecord.budget_group_daily_id > 0) {
              existingId = matchingRecord.budget_group_daily_id;
            }
          }

          // If no matching record found, try to find ANY record with an ID > 0 (for backwards compatibility)
          if (existingId === 0 && currentEditRow.budgetGroupRecords && currentEditRow.budgetGroupRecords.length > 0) {
            const recordWithId = currentEditRow.budgetGroupRecords.find(r => r.budget_group_daily_id > 0);
            if (recordWithId) {
              existingId = recordWithId.budget_group_daily_id;
            }
          }

          const records = [{
            budget_group_daily_id: existingId,
            ad_type: externalAdType,
            allocation_percent: 100,
          }];

          return records;
        }

        // For non-External groups, use existing budgetGroupRecords
        if (currentEditRow.budgetGroupRecords && currentEditRow.budgetGroupRecords.length > 0) {
          return currentEditRow.budgetGroupRecords;
        }

        // Otherwise, build from budget_group_daily_id_csv and ad_type_csv for editing
        if (currentEditRow.budget_group_daily_id_csv && currentEditRow.ad_type_csv) {
          const ids = currentEditRow.budget_group_daily_id_csv.split(',').map(id => parseInt(id.trim(), 10));
          const adTypes = currentEditRow.ad_type_csv.split(',').map(at => at.trim());
          const records = ids.map((id, index) => ({
            budget_group_daily_id: id,
            ad_type: adTypes[index] || adTypes[0],
            allocation_percent: 100 / ids.length, // Split evenly if multiple
          }));
          return records;
        }
        return [];
      })(),
      ...brandFields, // Add Brand-specific fields for Brand budgets
      ...dspFields, // Add DSP-specific fields for DSP budgets (these override any conflicts)
      // AUTO-DESERIALIZE platform-specific fields from budget_json using config
      // This replaces the manual mapping for Google/Meta/TikTok fields
      ...deserializeFromBudgetJson(budgetJson, retailerConfig.key, currentEditRow.budget_type),
      // Manual overrides for special cases
      ...(retailerConfig.key === 'google' && {
        googleBrandName: currentEditRow.brand_name || '', // Brand name comes from top-level column, not budget_json
        // Convert salesCountry value to select format
        ...(budgetJson.salesCountry && {
          googleSalesCountry: {
            label: budgetJson.salesCountry === 'US' ? 'United States' : budgetJson.salesCountry,
            value: budgetJson.salesCountry,
          },
        }),
      }),
      // For Meta, brand name comes from top-level column, not budget_json
      ...(retailerConfig.key === 'meta' && {
        metaBrandName: currentEditRow.brand_name || '',
      }),
      // For Meta, need to convert language values to select format and gender to select format
      ...(retailerConfig.key === 'meta' && budgetJson.languages && Array.isArray(budgetJson.languages) && {
        metaLanguages: budgetJson.languages.map(lang => {
          const languageMap = {
            en_US: 'English (US)',
            es_ES: 'Spanish (ES)',
            fr_FR: 'French (FR)',
            de_DE: 'German (DE)',
            it_IT: 'Italian (IT)',
            pt_BR: 'Portuguese (BR)',
            ja_JP: 'Japanese (JP)',
            ko_KR: 'Korean (KR)',
            zh_CN: 'Chinese (CN)',
          };
          return { label: languageMap[lang] || lang, value: lang };
        }),
      }),
      ...(retailerConfig.key === 'meta' && budgetJson.meta?.targeting?.genders && {
        metaGender: (() => {
          const value = budgetJson.meta.targeting.genders[0] || 0;
          const genderMap = {
            0: 'All Genders',
            1: 'Male',
            2: 'Female',
          };
          return { label: genderMap[value] || 'All Genders', value };
        })(),
      }),
      // For TikTok, need to convert language values to select format
      ...(retailerConfig.key === 'tiktok' && budgetJson.languages && Array.isArray(budgetJson.languages) && {
        tiktokLanguages: budgetJson.languages.map(lang => {
          const languageMap = {
            en_US: 'English (US)',
            es_ES: 'Spanish (ES)',
            fr_FR: 'French (FR)',
            de_DE: 'German (DE)',
            it_IT: 'Italian (IT)',
            pt_BR: 'Portuguese (BR)',
            ja_JP: 'Japanese (JP)',
            ko_KR: 'Korean (KR)',
            zh_CN: 'Chinese (CN)',
          };
          return { label: languageMap[lang] || lang, value: lang };
        }),
      }),
      ...activeDays, // Active days must be last to override any defaults from currentEditRow
    };

    return finalValues;
  };

  const handleSubmit = (formData) => {
    setDisableButton(true);
    const activeDaysArray = [];

    activeDayOptions.forEach(day => {
      activeDaysArray.push(formData[day.name] ? '1' : '0');
    });

    // Determine budget type
    const budgetType = formData.budgetType?.value || 'item';
    const isBrandBudget = budgetType === 'brand';
    const isDspBudget = budgetType === 'dsp';

    // Get budget allocation percentages
    // For brand budgets, allocation is 100% SB
    // For DSP budgets, allocation is 100% DSP (DSP can't share budget with other AD Types)
    // For Other budget type (External budgets), set allocation based on ad type
    let allocationSpSd = 0;
    let allocationSb = 0;
    let allocationSt = 0;
    let allocationDsp = 0;

    // Check if this is Other budget type and get its ad type
    const isOtherBudget = budgetType === 'other';
    let otherAdType = null;
    if (isOtherBudget && formData.budgetGroupRecords && formData.budgetGroupRecords.length > 0) {
      otherAdType = formData.budgetGroupRecords[0].ad_type;
    }

    if (isBrandBudget) {
      allocationSb = 100;
    } else if (isDspBudget) {
      // DSP budget type must be 100% DSP
      allocationDsp = 100;
    } else if (isOtherBudget) {
      // Other budget type (External budgets) - set allocation based on ad type
      if (otherAdType === 'DSP') {
        allocationDsp = 100;
      } else if (otherAdType === 'SB') {
        allocationSb = 100;
      } else if (otherAdType === 'ST') {
        allocationSt = 100;
      } else if (otherAdType === 'SP' || otherAdType === 'SD') {
        allocationSpSd = 100;
      } else {
        // Default to SP,SD if unknown
        allocationSpSd = 100;
      }
    } else {
      // Item budget
      allocationSpSd = parseFloat(formData.budgetAllocationSpSd) || 0;
      allocationSb = parseFloat(formData.budgetAllocationSb) || 0;
      allocationSt = parseFloat(formData.budgetAllocationSt) || 0;
    }

    // Check if we need to split items into individual budget groups
    if (formData.splitItemsCheckbox && formData.masterItemOnList) {
      // Parse the masterItemOnList to get individual items
      const itemList = formData.masterItemOnList
        .toString()
        .trim()
        .replace(/\r/g, '')
        .replace(/\s*,\s*/g, ',')
        .replace(/\n+/g, ',')
        .split(',')
        .filter(item => item.trim() !== '');

      if (itemList.length === 0) {
        setDisableButton(false);
        Notify.show('danger', 'Error', 'No items found in the master item list');
        return;
      }

      // Build original monthly values for split items
      const originalMonthly = {};
      monthOptions.forEach(month => {
        originalMonthly[month.name] = parseFloat(formData[month.name]) || 0;
      });

      // Create budget_json for split items (no allocation split)
      const itemBudgetJson = {
        allocation: {
          percentages: {
            sp_sd_percent: 100,
            sb_percent: 0,
            st_percent: 0,
          },
          original: {
            daily: {
              budget: parseFloat(formData.baseDailyBudgetAmount) || 0,
              reallocation: parseFloat(formData.reallocation) || 0,
            },
            monthly: originalMonthly,
          },
        },
        // AUTO-SERIALIZE root-level config fields (e.g. Negative/Custom Keywords)
        // so they are persisted on the split-items path as well.
        ...serializeToBudgetJson(formData, retailerConfig.key, formData.budgetType?.value || 'item'),
      };

      // Create a budget group for each item
      const postUrl = `${config.serverUrl}/accounts/budget-group-daily`;
      const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };

      let successCount = 0;
      let errorCount = 0;
      const promises = itemList.map((item) => {
        const itemBudgetGroupName = `${formData.budgetGroupName}_${item.trim()}`;

        // For split items, determine which ad types are active based on form selection
        // Default is 100% SP,SD (0% SB, 0% ST)
        const splitAdTypes = formData.adTypeCsv?.value || 'SP,SD';

        // Count how many ad types are in the selection to know how many 0s to send
        const adTypeCount = splitAdTypes.split(',').filter(at => at.trim()).length;
        const splitIds = Array(adTypeCount).fill(0).join(','); // e.g., "0" or "0,0" or "0,0,0"

        const submitBudgetGroupDaily = {
          accountId: sellerSelect.value,
          accountTypeId: sellerSelect.type,
          budgetGroupDailyIdCsv: splitIds, // CSV of 0s for new budget groups
          budgetGroupName: itemBudgetGroupName,
          setupFlow: formData.setupFlow?.value,
          retailer: retailerConfig.key,
          agencyInternal: formData.agencyInternal?.value,
          activeDaysCsv: activeDaysArray.join(','),
          adTypeCsv: splitAdTypes,
          portfolioIdsCsv: formData.portfolioIdsCsv,
          includes: formData.includes.value,
          costTargetType: formData.costTargetType.value,
          costTargetMetric: 'roas',
          costTargetBrand: formData.costTargetType.value === 'bnb' ? formData.costTargetBrand : formData.costTarget,
          costTargetNonBrand: formData.costTargetType.value === 'bnb' ? formData.costTargetNonBrand : formData.costTarget,
          spendSpeed: formData.spendSpeed.value,
          spendTactic: formData.spendTactic.value,
          limitlessBudget: formData.limitlessBudget.value,
          spendPriority: formData.spendPriority.value,
          itemType: formData.itemType?.value,
          itemSelection: formData.itemSelection?.value,
          destination: formData.destination?.value,
          masterItemOrigList: item.trim(),
          masterItemOnList: item.trim(),
          masterItemOffList: '',
          startDate: formData.startDate,
          endDate: formData.endDate,
          budgetDriver: formData.budgetDriver.value,
          budgetJson: itemBudgetJson,
          state: formData.state.value,
          budgetType: formData.budgetType?.value || 'item',
        };

        const requestOptions = {
          method: 'POST',
          headers: headerWithAuth,
          body: JSON.stringify(submitBudgetGroupDaily),
        };

        return fetch(postUrl, requestOptions)
          .then((results) => {
            if (!results.ok) {
              throw Error(results.statusText);
            }
            successCount += 1;
            return results.json();
          })
          .catch((error) => {
            errorCount += 1;
            console.error(`Error creating budget group for ${item}:`, error);
            return null;
          });
      });

      Promise.all(promises)
        .then(() => {
          changeShowEditModal();
          setDisableButton(false);
          setForceRefresh(moment().utc());

          if (successCount > 0) {
            Notify.show('success', 'Success', `Successfully created ${successCount} budget group(s)`);
          }
          if (errorCount > 0) {
            Notify.show('warning', 'Warning', `Failed to create ${errorCount} budget group(s)`);
          }
        })
        .catch((error) => {
          Notify.show('danger', 'Error', `An error occurred while creating budget groups: ${error}`);
          console.error(error);
          setDisableButton(false);
        });
      return;
    }

    // Make single API call - backend will handle the structured budgetGroupRecords
    if (formData.budgetGroupName) {
      const postUrl = `${config.serverUrl}/accounts/budget-group-daily`;
      const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };

      // Check if this is an External budget group first
      const isExternalBudgetGroup = formData.budgetGroupName && formData.budgetGroupName.trim().endsWith('- External');

      // Build budgetGroupRecords with allocation percentages
      // DSP budgets use a single DSP record with 100% allocation
      // External budget groups use a single record with determined ad type and 100% allocation
      let budgetGroupRecords;
      if (isExternalBudgetGroup) {
        // Determine ad type based on budget name content for External groups
        const budgetName = formData.budgetGroupName;
        let externalAdType = 'SP'; // Default

        if (budgetName.includes('Sponsored Brands')) {
          externalAdType = 'SB';
        } else if (budgetName.includes('Sponsored Products')) {
          externalAdType = 'SP';
        } else if (budgetName.includes('Sponsored Display')) {
          externalAdType = 'SD';
        } else if (budgetName.includes('Sponsored Television')) {
          externalAdType = 'ST';
        } else if (budgetName.includes('DSP')) {
          externalAdType = 'DSP';
        }

        // Always use the determined ad type for External groups, even when editing
        // Get the budget_group_daily_id from existing records if editing
        const existingId = formData.budgetGroupRecords && formData.budgetGroupRecords.length > 0
          ? formData.budgetGroupRecords[0].budget_group_daily_id
          : 0;

        budgetGroupRecords = [
          { budget_group_daily_id: existingId, ad_type: externalAdType, allocation_percent: 100 },
        ];
      } else if (isDspBudget) {
        budgetGroupRecords = formData.budgetGroupRecords || [
          { budget_group_daily_id: 0, ad_type: 'DSP', allocation_percent: 100 },
        ];
      } else if (retailerConfig.key !== 'amazon') {
        // Non-Amazon platforms (Google, Meta, TikTok, etc.): single record with 100% allocation
        const selectedBudgetType = formData.budgetType?.value;
        const platformConfig = PLATFORM_BUDGET_TYPES[retailerConfig.key];
        const budgetTypeOption = platformConfig?.options?.find(opt => opt.value === selectedBudgetType);
        const adType = budgetTypeOption?.adType || selectedBudgetType?.toUpperCase() || 'UNKNOWN';

        // Get existing ID from currentEditRow (budgetGroupRecords is not a form field)
        let existingId = 0;
        if (currentEditRow && currentEditRow.budget_group_daily_id_csv) {
          // Parse the ID from CSV (should be single ID for non-Amazon)
          const ids = currentEditRow.budget_group_daily_id_csv.split(',').map(id => parseInt(id.trim(), 10));
          existingId = ids[0] || 0;
        }

        budgetGroupRecords = [
          { budget_group_daily_id: existingId, ad_type: adType, allocation_percent: 100 },
        ];
      } else {
        // Amazon platform: standard allocation logic with multiple ad types
        budgetGroupRecords = formData.budgetGroupRecords || [
          { budget_group_daily_id: 0, ad_type: 'SP,SD', allocation_percent: allocationSpSd },
          { budget_group_daily_id: 0, ad_type: 'SB', allocation_percent: allocationSb },
          { budget_group_daily_id: 0, ad_type: 'ST', allocation_percent: allocationSt },
        ];
      }

      // Build original monthly values
      const originalMonthly = {};
      monthOptions.forEach(month => {
        originalMonthly[month.name] = parseFloat(formData[month.name]) || 0;
      });

      // Create budget_json with only allocation and original values
      // Backend will handle daily and monthly node calculations
      const budgetJson = {
        // Only include allocation for Amazon platform (other platforms have simple 100% allocation per budget)
        ...(retailerConfig.key === 'amazon' && {
          allocation: {
            percentages: {
              sp_sd_percent: allocationSpSd,
              sb_percent: allocationSb,
              st_percent: allocationSt,
              dsp_percent: allocationDsp, // DSP must be 100% if budget type is DSP
            },
            original: {
              daily: {
                budget: parseFloat(formData.baseDailyBudgetAmount) || 0,
                reallocation: parseFloat(formData.reallocation) || 0,
              },
              monthly: originalMonthly,
            },
          },
        }),
        // For non-Amazon platforms, just store daily/monthly budgets directly
        ...(retailerConfig.key !== 'amazon' && {
          daily: {
            budget: parseFloat(formData.baseDailyBudgetAmount) || 0,
          },
          monthly: originalMonthly,
        }),
        // AUTO-SERIALIZE platform-specific fields using config
        // This replaces the manual mapping for Google/Meta/TikTok/DSP fields
        ...serializeToBudgetJson(formData, retailerConfig.key, budgetType, {
          // DSP-specific metadata (keep existing structure for backwards compatibility)
          ...(isDspBudget && {
            dsp: {
              gender: formData.dspGender?.value,
              ad_format: formData.dspAdFormat?.value,
              spend_tactic: formData.dspSpendTactic?.value,
              spend_speed: formData.dspSpendSpeed?.value,
            },
          }),
        }),
      };

      // Build adTypeCsv and budgetGroupDailyIdCsv from budgetGroupRecords for backend compatibility
      // Use the isExternalBudgetGroup variable already defined above
      let adTypeCsv;
      let budgetGroupDailyIdCsv;

      if (isExternalBudgetGroup) {
        // Determine ad type based on budget name content
        const budgetName = formData.budgetGroupName;
        if (budgetName.includes('Sponsored Brands')) {
          adTypeCsv = 'SB';
        } else if (budgetName.includes('Sponsored Products')) {
          adTypeCsv = 'SP';
        } else if (budgetName.includes('Sponsored Display')) {
          adTypeCsv = 'SD';
        } else if (budgetName.includes('Sponsored Television')) {
          adTypeCsv = 'ST';
        } else {
          // Default to SP if no match
          adTypeCsv = 'SP';
        }
        // For External groups, use the single ID from budgetGroupRecords
        budgetGroupDailyIdCsv = budgetGroupRecords[0].budget_group_daily_id.toString();
      } else if (retailerConfig.key !== 'amazon') {
        // Non-Amazon platforms: simple mapping from budgetGroupRecords (always single record with 100%)
        const activeRecords = budgetGroupRecords.filter(r => r.allocation_percent > 0);
        adTypeCsv = activeRecords.map(r => r.ad_type).join(',');
        budgetGroupDailyIdCsv = activeRecords.map(r => r.budget_group_daily_id).join(',');
      } else if (currentEditRow && currentEditRow.ad_type_csv && currentEditRow.budget_group_daily_id_csv) {
        // Normal budget groups - IMPORTANT: preserve original API order!
        // budgetGroupRecords might be in standard order, but we need to match the original API order
        // The original API order is preserved in currentEditRow.ad_type_csv and budget_group_daily_id_csv

        // Editing existing budget - use original order from currentEditRow
        // Use the original order from the API
        const originalAdTypes = currentEditRow.ad_type_csv.split(',').map(at => at.trim());
        const originalIds = currentEditRow.budget_group_daily_id_csv.split(',').map(id => id.trim());

        // Build arrays in original API order
        // Note: SP and SD are ALWAYS grouped together in non-External budgets
        // So if we see "SP,SD,SB", the IDs would be [id_for_SP_SD, id_for_SB]
        const orderedAdTypes = [];
        const orderedIds = [];

        let idIndex = 0; // Track which ID we're on
        let i = 0;

        while (i < originalAdTypes.length) {
          const originalAdType = originalAdTypes[i];

          // Check if this is SP or SD (they're always paired together)
          if (originalAdType === 'SP' || originalAdType === 'SD') {
            // Find the SP,SD record in budgetGroupRecords
            const record = budgetGroupRecords.find(r => r.ad_type === 'SP,SD');

            // Only include if allocation > 0
            if (record && record.allocation_percent > 0) {
              // Check if the next item is also SP or SD (they should be consecutive)
              if (i + 1 < originalAdTypes.length && (originalAdTypes[i + 1] === 'SP' || originalAdTypes[i + 1] === 'SD')) {
                // Both SP and SD are present in original order
                orderedAdTypes.push(originalAdTypes[i], originalAdTypes[i + 1]);
                orderedIds.push(originalIds[idIndex] || '0', originalIds[idIndex] || '0'); // Same ID for both
                i += 2; // Skip both SP and SD
              } else {
                // Only one of SP or SD is present
                orderedAdTypes.push(originalAdType);
                orderedIds.push(originalIds[idIndex] || '0');
                i += 1;
              }
            } else if (i + 1 < originalAdTypes.length && (originalAdTypes[i + 1] === 'SP' || originalAdTypes[i + 1] === 'SD')) {
              // SP,SD allocation is 0, skip both SP and SD
              i += 2;
            } else {
              // SP,SD allocation is 0, skip one
              i += 1;
            }
            idIndex += 1; // Move to next ID
          } else {
            // SB, ST, or other ad types
            const record = budgetGroupRecords.find(r => r.ad_type === originalAdType);

            // Only include if allocation > 0
            if (record && record.allocation_percent > 0) {
              orderedAdTypes.push(originalAdType);
              orderedIds.push(originalIds[idIndex] || '0');
            }
            idIndex += 1; // Move to next ID
            i += 1;
          }
        }

        adTypeCsv = orderedAdTypes.join(',');
        budgetGroupDailyIdCsv = orderedIds.join(',');
      } else {
        // New budget - use standard order
        const activeRecords = budgetGroupRecords.filter(r => r.allocation_percent > 0);
        adTypeCsv = activeRecords.map(r => r.ad_type).join(',');
        budgetGroupDailyIdCsv = activeRecords.map(r => r.budget_group_daily_id).join(',');
      }

      const submitBudgetGroupDaily = {
        accountId: sellerSelect.value,
        accountTypeId: sellerSelect.type,
        budgetGroupRecords, // Send structured records array
        budgetGroupDailyIdCsv, // Also send CSV format for backwards compatibility
        budgetGroupName: formData.budgetGroupName,
        setupFlow: formData.setupFlow?.value,
        retailer: retailerConfig.key,
        agencyInternal: formData.agencyInternal?.value,
        activeDaysCsv: activeDaysArray.join(','),
        adTypeCsv, // Backend still needs this for compatibility
        portfolioIdsCsv: isDspBudget ? '' : (formData.portfolioIdsCsv || ''),
        includes: isDspBudget ? (formData.dspGender?.value || '') : (formData.includes?.value || ''), // For DSP, includes stores gender
        costTargetType: formData.costTargetType.value,
        costTargetMetric: 'roas',
        costTargetBrand: formData.costTargetType.value === 'bnb' ? formData.costTargetBrand : formData.costTarget,
        costTargetNonBrand: formData.costTargetType.value === 'bnb' ? formData.costTargetNonBrand : formData.costTarget,
        spendSpeed: isDspBudget ? (formData.dspSpendSpeed?.value || 'speed_level_1') : (formData.spendSpeed?.value || 'speed_level_1'),
        spendTactic: isDspBudget ? (formData.dspSpendTactic?.value || 'max_perf') : (formData.spendTactic?.value || 'max_perf'),
        limitlessBudget: formData.limitlessBudget?.value || 0,
        spendPriority: formData.spendPriority.value,
        itemType: isDspBudget ? formData.dspItemType?.value : formData.itemType?.value,
        itemSelection: formData.itemSelection?.value,
        destination: formData.destination?.value,
        masterItemOrigList: formData.masterItemOrigList || formData.masterItemOnList || '',
        masterItemOnList: formData.masterItemOnList || '', // For DSP this is the landing page URL
        masterItemOffList: formData.masterItemOffList || '',
        startDate: formData.startDate,
        endDate: formData.endDate,
        budgetDriver: formData.budgetDriver.value,
        budgetJson, // Includes allocation data and DSP metadata
        state: formData.state.value,
        budgetType,
        // Brand budget fields
        ...(isBrandBudget && {
          adFormat: formData.brandAdFormat?.value,
          brandName: formData.brandName,
          brandEntityId: formData.brandEntityId,
        }),
        // DSP budget fields - adFormat stores the DSP ad format
        ...(isDspBudget && {
          adFormat: formData.dspAdFormat?.value || 'display',
        }),
        // Google budget fields - brandName
        ...(retailerConfig.key === 'google' && {
          brandName: formData.googleBrandName || '',
        }),
        // Meta budget fields - brandName
        ...(retailerConfig.key === 'meta' && {
          brandName: formData.metaBrandName || '',
        }),
      };

      const requestOptions = {
        method: 'POST',
        headers: headerWithAuth,
        body: JSON.stringify(submitBudgetGroupDaily),
      };

      // Single API call
      fetch(postUrl, requestOptions)
        .then((results) => {
          if (!results.ok) {
            throw Error(results.statusText);
          }
          return results.json();
        })
        .then(() => {
          changeShowEditModal();
          setDisableButton(false);
          setForceRefresh(moment().utc());
          Notify.show('success', 'Success', 'Budget group saved successfully');
        })
        .catch((error) => {
          Notify.show('danger', 'Error', `We were unable to update your budget. Error: ${error}`);
          console.error(error);
          setDisableButton(false);
        });
    }
  };

  const modalClasses = classNames({
    'todo__add-modal': true,
  }, theme.className, 'ltr-support');

  return (
    <Modal
      isOpen={showEditModal}
      toggle={changeShowEditModal}
      className={modalClasses}
    >
      <GroupEditModalForm
        onSubmit={handleSubmit}
        initialValues={getInitialValues()}
        currentEditRow={currentEditRow}
        changeShowEditModal={changeShowEditModal}
        activeDayOptions={activeDayOptions}
        monthOptions={monthOptions}
        user={user}
        budgetAccountData={budgetAccountData}
        disableButton={disableButton}
        setDisableButton={setDisableButton}
        setShowEditModal={setShowEditModal}
        retailerConfig={retailerConfig}
      />
    </Modal>
  );
};

GroupEditModal.propTypes = {
  currentEditRow: PropTypes.shape(),
  changeShowEditModal: PropTypes.func.isRequired,
  showEditModal: PropTypes.bool.isRequired,
  setForceRefresh: PropTypes.func.isRequired,
};

GroupEditModal.defaultProps = {
  currentEditRow: null,
};

export default GroupEditModal;
